import React, { useState, useEffect } from 'react';
import './Admin.css';
import { db, storage } from '../../firebase';
import {
    collection, deleteDoc, doc, setDoc, getDoc, updateDoc,
    serverTimestamp, query, orderBy, addDoc, onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const PLAN_LABELS = {
    'starter': 'Start (Low-Cost)',
    'pro': 'Pro Lunar',
    'transform': 'Transformare',
    'inactive': 'Fără Plan / Inactiv'
};

const ADMIN_EMAIL = "hadade.cosmin@yahoo.com";

// Extrage ID-ul din orice format de link YouTube (watch, youtu.be, shorts, embed, live)
const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
        /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/
    );
    return match ? match[1] : null;
};

// --- SUB-COMPONENT: AdminReviewItem ---
const AdminReviewItem = ({ r, onDelete }) => {
    const [beforeFile, setBeforeFile] = useState(null);
    const [afterFile, setAfterFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleUploadTransformation = async () => {
        if (!beforeFile || !afterFile) {
            alert("Te rog selectează ambele poze (Before și After).");
            return;
        }
        setIsUploading(true);
        try {
            const beforeRef = ref(storage, `transformations/${r.id}_before_${Date.now()}`);
            await uploadBytes(beforeRef, beforeFile);
            const beforeUrl = await getDownloadURL(beforeRef);

            const afterRef = ref(storage, `transformations/${r.id}_after_${Date.now()}`);
            await uploadBytes(afterRef, afterFile);
            const afterUrl = await getDownloadURL(afterRef);

            const reviewRef = doc(db, "testimonials", r.id);
            await updateDoc(reviewRef, {
                beforePhoto: beforeUrl,
                afterPhoto: afterUrl
            });

            alert("Transformare adăugată cu succes!");
            setBeforeFile(null);
            setAfterFile(null);
        } catch (error) {
            console.error(error);
            alert("Eroare la upload: " + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="admin-review-card">
            <div className="review-header">
                <strong>{r.userName}</strong>
                <span className="stars">{"⭐".repeat(r.rating)}</span>
            </div>
            <p>"{r.text}"</p>
            <div style={{ marginTop: '15px', background: '#333', padding: '10px', borderRadius: '5px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#ccff00' }}>Adaugă Transformare (Before/After)</h4>
                {r.beforePhoto && r.afterPhoto && <div style={{ marginBottom: '10px', color: '#888', fontSize: '0.8rem' }}>✅ Are poze.</div>}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <small>Before:</small>
                        <input type="file" accept="image/*" onChange={(e) => setBeforeFile(e.target.files[0])} style={{ fontSize: '0.7rem' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <small>After:</small>
                        <input type="file" accept="image/*" onChange={(e) => setAfterFile(e.target.files[0])} style={{ fontSize: '0.7rem' }} />
                    </div>
                </div>
                <button onClick={handleUploadTransformation} disabled={isUploading} style={{ background: '#4CAF50', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', fontSize: '0.8rem' }}>
                    {isUploading ? "Uploading..." : "💾 Salvează Poze"}
                </button>
            </div>
            <button className="btn-delete" style={{marginTop: '10px'}} onClick={() => onDelete(r.id)}>Șterge Review</button>
        </div>
    );
};

function Admin({ user }) {
    const [activeTab, setActiveTab] = useState('users');

    // --- DATA STATE ---
    const [users, setUsers] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [articles, setArticles] = useState([]);
    const [trainPosts, setTrainPosts] = useState([]);

    // --- STATE ARTICOLE (BLOG STYLE) ---
    const [articleTitle, setArticleTitle] = useState("");
    const [articleBlocks, setArticleBlocks] = useState([]); // Blocks for Blog

    // --- STATE ALTELE ---
    const [isUploading, setIsUploading] = useState(false);
    const [selectedCoachingUser, setSelectedCoachingUser] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(false);
    const [isSavingPlan, setIsSavingPlan] = useState(false);
    const [dietBlocks, setDietBlocks] = useState([]);
    const [workoutBlocks, setWorkoutBlocks] = useState([]);
    const [filesToDelete, setFilesToDelete] = useState([]);
    const [trainTitle, setTrainTitle] = useState("");
    const [trainDesc, setTrainDesc] = useState("");
    const [trainFiles, setTrainFiles] = useState([]);
    const [trainYtLinks, setTrainYtLinks] = useState([]); // linkuri YouTube pt postarea nouă
    const [postYtInputs, setPostYtInputs] = useState({}); // input per postare existentă {postId: text}
    const [isUploadingTrain, setIsUploadingTrain] = useState(false);

    // ==========================
    // 1. FETCH DATA LIVE
    // ==========================
    useEffect(() => {
        if (user?.email !== ADMIN_EMAIL) return;
        let unsubscribe;

        const setupListener = async () => {
            try {
                if (activeTab === 'users' || activeTab === 'coaching') {
                    const q = query(collection(db, "users"));
                    unsubscribe = onSnapshot(q, (snapshot) => {
                        setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
                    });
                }
                else if (activeTab === 'reviews') {
                    const q = query(collection(db, "testimonials"), orderBy("createdAt", "desc"));
                    unsubscribe = onSnapshot(q, (snapshot) => {
                        setReviews(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
                    });
                }
                else if (activeTab === 'articles') {
                    const q = query(collection(db, "articles"), orderBy("createdAt", "desc"));
                    unsubscribe = onSnapshot(q, (snapshot) => {
                        setArticles(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
                    });
                }
                else if (activeTab === 'traintravel') {
                    const q = query(collection(db, "traintravel"), orderBy("createdAt", "desc"));
                    unsubscribe = onSnapshot(q, (snapshot) => {
                        setTrainPosts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
                    });
                }
            } catch (error) {
                console.error("Eroare la fetch:", error);
            }
        };

        setupListener();
        return () => { if (unsubscribe) unsubscribe(); };
    }, [user, activeTab]);

    // ==========================
    // HELPERS PENTRU BLOCK EDITOR (FOLOSIT SI LA COACHING SI LA ARTICOLE)
    // ==========================
    const addBlock = (setBlocks, type) => setBlocks(prev => [...prev, { id: Date.now(), type, content: "", url: "", file: null }]);

    const removeBlock = (setBlocks, id, currentBlocks) => {
        const b = currentBlocks.find(item => item.id === id);
        // Dacă e un url de firebase, îl marcăm pt ștergere (doar la coaching, la articole ștergem direct sau lăsăm orfan)
        if(b?.url?.includes("firebasestorage") && setFilesToDelete) {
            setFilesToDelete(prev => [...prev, b.url]);
        }
        setBlocks(prev => prev.filter(item => item.id !== id));
    };

    const updateBlockContent = (setBlocks, id, text) => setBlocks(prev => prev.map(b => b.id === id ? { ...b, content: text } : b));

    const updateBlockUrl = (setBlocks, id, url) => setBlocks(prev => prev.map(b => b.id === id ? { ...b, url } : b));

    const handleBlockFileChange = (setBlocks, id, file) => {
        if (!file) return;
        setBlocks(prev => prev.map(b => b.id === id ? { ...b, file: file, url: URL.createObjectURL(file) } : b));
    };

    // ==========================
    // 2. LOGICA ARTICOLE (BLOG STYLE)
    // ==========================
    const handlePublishArticle = async (e) => {
        e.preventDefault();
        if (!articleTitle) return alert("Adaugă un titlu!");

        // Validare blocuri YouTube
        for (let block of articleBlocks) {
            if (block.type === 'youtube' && !getYouTubeId(block.url)) {
                return alert("Ai un bloc YouTube cu link invalid!");
            }
        }

        setIsUploading(true);
        try {
            const finalBlocks = [];

            // Procesăm fiecare bloc
            for (let block of articleBlocks) {
                let finalUrl = block.url;

                // Dacă avem fișier nou selectat, facem upload
                if (block.file) {
                    const storageRef = ref(storage, `articles/${Date.now()}_${block.file.name}`);
                    await uploadBytes(storageRef, block.file);
                    finalUrl = await getDownloadURL(storageRef);
                }

                // Construim obiectul curat pentru DB
                finalBlocks.push({
                    type: block.type, // 'text', 'image', 'video', 'youtube'
                    content: block.content || "", // textul
                    url: finalUrl || "" // url-ul media / link YouTube
                });
            }

            await addDoc(collection(db, "articles"), {
                title: articleTitle,
                blocks: finalBlocks, // NOUA STRUCUTRA BLOG
                createdAt: serverTimestamp()
            });

            alert("Articol Blog Publicat!");
            setArticleTitle("");
            setArticleBlocks([]);
        } catch (e) {
            console.error(e);
            alert("Eroare: " + e.message);
        }
        setIsUploading(false);
    };

    const handleDeleteArticle = async (id) => { if(window.confirm("Delete?")) await deleteDoc(doc(db, "articles", id)); };


    // ==========================
    // 3. USER ACTIONS & COACHING
    // ==========================
    const handleUpdatePlan = async (userId, newPlan) => {
        try {
            const userRef = doc(db, "users", userId);
            const newStatus = newPlan === 'inactive' ? 'inactive' : 'active';
            await setDoc(userRef, { subscriptionPlan: newPlan, subscriptionStatus: newStatus }, { merge: true });
        } catch (error) { console.error(error); alert("Eroare actualizare plan."); }
    };

    const handleBanUser = async (userId) => {
        if(window.confirm("Ești sigur că vrei să ștergi acest utilizator și toate datele lui?")) {
            try { await deleteDoc(doc(db, "users", userId)); } catch (error) { alert("Eroare la ștergere user."); }
        }
    };

    const handleSelectCoachingUser = async (client) => {
        setSelectedCoachingUser(client);
        setLoadingPlan(true);
        setDietBlocks([]);
        setWorkoutBlocks([]);
        setFilesToDelete([]);
        try {
            const planRef = doc(db, "users", client.id, "plans", "current_plan");
            const planSnap = await getDoc(planRef);
            if (planSnap.exists()) {
                const data = planSnap.data();
                setDietBlocks(data.dietBlocks || []);
                setWorkoutBlocks(data.workoutBlocks || []);
            }
        } catch (error) { console.error("Eroare:", error); }
        setLoadingPlan(false);
    };

    const processBlocksForUpload = async (blocks, folderName) => {
        const processed = [];
        for (let block of blocks) {
            let finalUrl = block.url;
            if (block.file) {
                const storageRef = ref(storage, `plans/${selectedCoachingUser.id}/${folderName}/${Date.now()}_${block.file.name}`);
                await uploadBytes(storageRef, block.file);
                finalUrl = await getDownloadURL(storageRef);
            }
            processed.push({ id: block.id, type: block.type, content: block.content || "", url: finalUrl || "" });
        }
        return processed;
    };

    const handleSavePlan = async () => {
        if (!selectedCoachingUser) return;

        // Validare blocuri YouTube
        for (let block of [...dietBlocks, ...workoutBlocks]) {
            if (block.type === 'youtube' && !getYouTubeId(block.url)) {
                return alert("Ai un bloc YouTube cu link invalid!");
            }
        }

        setIsSavingPlan(true);
        try {
            if (filesToDelete.length > 0) {
                await Promise.all(filesToDelete.map(async (url) => { try { await deleteObject(ref(storage, url)); } catch (e) {} }));
                setFilesToDelete([]);
            }
            const finalDiet = await processBlocksForUpload(dietBlocks, 'diet');
            const finalWorkout = await processBlocksForUpload(workoutBlocks, 'workout');
            await setDoc(doc(db, "users", selectedCoachingUser.id, "plans", "current_plan"), {
                dietBlocks: finalDiet, workoutBlocks: finalWorkout, updatedAt: serverTimestamp(), updatedBy: "Admin"
            }, { merge: true });
            alert(`Plan salvat pentru ${selectedCoachingUser.fullName}!`);
        } catch (error) { alert("Eroare: " + error.message); } finally { setIsSavingPlan(false); }
    };

    const handleDeleteEntirePlan = async () => {
        if (!selectedCoachingUser || !window.confirm("RESET?")) return;
        setIsSavingPlan(true);
        try {
            await setDoc(doc(db, "users", selectedCoachingUser.id, "plans", "current_plan"), { dietBlocks: [], workoutBlocks: [], updatedAt: serverTimestamp() });
            setDietBlocks([]); setWorkoutBlocks([]);
            alert("Plan resetat!");
        } catch (e) { alert(e.message); } finally { setIsSavingPlan(false); }
    };

    const handleDeleteReview = async (id) => { if(window.confirm("Delete?")) await deleteDoc(doc(db, "testimonials", id)); };


    // ==========================
    // 4. TRAIN & TRAVEL
    // ==========================
    const handlePublishTrainTravel = async (e) => {
        e.preventDefault();

        // Validare linkuri YouTube (ignorăm câmpurile goale)
        const filledLinks = trainYtLinks.map(l => l.trim()).filter(Boolean);
        for (let link of filledLinks) {
            if (!getYouTubeId(link)) return alert("Ai un link YouTube invalid: " + link);
        }

        setIsUploadingTrain(true);
        try {
            const mediaItems = [];
            for(let i=0; i<trainFiles.length; i++) {
                const file = trainFiles[i]; const isVideo = file.type.startsWith('video');
                const storageRef = ref(storage, `traintravel/${Date.now()}_${i}_${file.name}`);
                await uploadBytes(storageRef, file);
                mediaItems.push({ url: await getDownloadURL(storageRef), type: isVideo ? 'video' : 'image' });
            }
            await addDoc(collection(db, "traintravel"), {
                title: trainTitle,
                media: mediaItems,
                youtube: filledLinks, // array de linkuri YouTube
                description: trainDesc,
                createdAt: serverTimestamp()
            });
            alert("Postat!"); setTrainTitle(""); setTrainFiles([]); setTrainDesc(""); setTrainYtLinks([]);
            const fileInput = document.getElementById('fileInputTT');
            if (fileInput) fileInput.value = "";
        } catch(e) { alert("Eroare"); } setIsUploadingTrain(false);
    };
    const handleDeleteTrainPost = async (id) => { if(window.confirm("Delete?")) await deleteDoc(doc(db, "traintravel", id)); };
    const handleDeleteMedia = async (postId, item) => {
        if (!window.confirm("Ștergi fișierul?")) return;
        try {
            try { await deleteObject(ref(storage, item.url)); } catch (e) {}
            const post = trainPosts.find(p => p.id === postId);
            const updatedMedia = post.media.filter(m => m.url !== item.url);
            await updateDoc(doc(db, "traintravel", postId), { media: updatedMedia });
        } catch (e) { alert("Eroare"); }
    };

    // Adaugă un link YouTube la o postare existentă
    const handleAddYtToPost = async (postId) => {
        const url = (postYtInputs[postId] || "").trim();
        if (!getYouTubeId(url)) return alert("Link YouTube invalid!");
        try {
            const post = trainPosts.find(p => p.id === postId);
            const current = post.youtube || [];
            if (current.includes(url)) return alert("Linkul există deja la această postare.");
            await updateDoc(doc(db, "traintravel", postId), { youtube: [...current, url] });
            setPostYtInputs(prev => ({ ...prev, [postId]: "" }));
        } catch (err) { alert("Eroare: " + err.message); }
    };

    // Șterge un link YouTube de la o postare existentă
    const handleRemoveYtFromPost = async (postId, url) => {
        if (!window.confirm("Ștergi clipul YouTube?")) return;
        try {
            const post = trainPosts.find(p => p.id === postId);
            const updated = (post.youtube || []).filter(l => l !== url);
            await updateDoc(doc(db, "traintravel", postId), { youtube: updated });
        } catch (err) { alert("Eroare: " + err.message); }
    };

    // ==========================
    // COMPONENTA UI: EDITOR BLOCURI
    // ==========================
    const renderBlockEditor = (blocks, setBlocks, title) => (
        <div className="block-editor-container">
            <h3>{title}</h3>
            <div className="block-controls">
                <button type="button" onClick={() => addBlock(setBlocks, 'text')}>+ Text</button>
                <button type="button" onClick={() => addBlock(setBlocks, 'image')}>+ Poză</button>
                <button type="button" onClick={() => addBlock(setBlocks, 'video')}>+ Video</button>
                <button type="button" onClick={() => addBlock(setBlocks, 'youtube')}>+ YouTube</button>
            </div>
            <div className="blocks-list">
                {blocks.map((block, index) => (
                    <div key={block.id} className="admin-block-item">
                        <div className="block-header">
                            <span>{block.type.toUpperCase()} #{index + 1}</span>
                            <button type="button" className="btn-delete-small" onClick={() => removeBlock(setBlocks, block.id, blocks)}>❌</button>
                        </div>
                        {block.type === 'text' && <textarea rows="3" placeholder="Scrie text aici..." value={block.content} onChange={(e) => updateBlockContent(setBlocks, block.id, e.target.value)} />}
                        {(block.type === 'image' || block.type === 'video') && (
                            <div className="media-block-input">
                                <input type="file" accept={block.type === 'video' ? "video/*" : "image/*"} onChange={(e) => handleBlockFileChange(setBlocks, block.id, e.target.files[0])} />
                                {block.url && (block.type === 'video' ? <video src={block.url} controls height="100" /> : <img src={block.url} alt="preview" height="100" />)}
                            </div>
                        )}
                        {block.type === 'youtube' && (
                            <div className="media-block-input">
                                <input
                                    type="text"
                                    placeholder="Lipește link YouTube..."
                                    value={block.url}
                                    onChange={(e) => updateBlockUrl(setBlocks, block.id, e.target.value)}
                                />
                                {getYouTubeId(block.url) && (
                                    <iframe
                                        width="220" height="124"
                                        src={`https://www.youtube.com/embed/${getYouTubeId(block.url)}`}
                                        title="preview" frameBorder="0" allowFullScreen
                                    />
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    if (!user || user.email !== ADMIN_EMAIL) return <div className="admin-denied"><h1>⛔ ACCESS DENIED</h1></div>;

    return (
        <div className="admin-container">
            <div className="admin-sidebar">
                <h2 className="admin-logo">HC Admin</h2>
                <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>👥 Useri</button>
                <button className={activeTab === 'coaching' ? 'active' : ''} onClick={() => setActiveTab('coaching')}>💪 Coaching</button>
                <button className={activeTab === 'reviews' ? 'active' : ''} onClick={() => setActiveTab('reviews')}>⭐ Recenzii</button>
                <button className={activeTab === 'articles' ? 'active' : ''} onClick={() => setActiveTab('articles')}>📝 Articole</button>
                <button className={activeTab === 'traintravel' ? 'active' : ''} onClick={() => setActiveTab('traintravel')}>✈️ Train & Travel</button>
            </div>

            <div className="admin-content">
                {/* --- USERS TAB --- */}
                {activeTab === 'users' && (
                    <div className="admin-section">
                        <h2>Gestionare Utilizatori ({users.length})</h2>
                        <div className="table-responsive">
                            <table className="admin-table">
                                <thead>
                                <tr>
                                    <th>Nume</th>
                                    <th>Email</th>
                                    <th>Vârstă</th>
                                    <th>Status</th>
                                    <th>Plan Abonament</th>
                                    <th>Acțiune</th>
                                </tr>
                                </thead>
                                <tbody>
                                {users.map(u => (
                                    <tr key={u.id} style={{ opacity: u.subscriptionStatus === 'inactive' ? 0.6 : 1 }}>
                                        <td>{u.fullName || "No Name"}</td>
                                        <td>{u.email}</td>
                                        <td>{u.age || u.varsta || '-'}</td>
                                        <td>
                                            <span style={{
                                                padding: '5px 10px', borderRadius: '5px', fontSize: '0.8rem', fontWeight: 'bold',
                                                background: u.subscriptionStatus === 'active' ? '#ccff00' : '#444',
                                                color: u.subscriptionStatus === 'active' ? 'black' : '#ccc'
                                            }}>
                                                {u.subscriptionStatus === 'active' ? 'ACTIV' : 'INACTIV'}
                                            </span>
                                        </td>
                                        <td>
                                            <select
                                                className="admin-select-sm"
                                                value={u.subscriptionPlan || 'inactive'}
                                                onChange={(e) => handleUpdatePlan(u.id, e.target.value)}
                                                style={{border: u.subscriptionStatus === 'active' ? '1px solid #ccff00' : '1px solid #555'}}
                                            >
                                                <option value="inactive">⛔ INACTIV</option>
                                                <option value="starter">Starter</option>
                                                <option value="pro">Pro</option>
                                                <option value="transform">Transformare</option>
                                            </select>
                                        </td>
                                        <td>
                                            <button className="btn-ban" onClick={() => handleBanUser(u.id)}>BAN</button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* --- COACHING TAB --- */}
                {activeTab === 'coaching' && (
                    <div className="admin-section coaching-layout">
                        <div className="coaching-list">
                            <h3>Clienți</h3>
                            <div className="client-scroll">
                                {users.map(u => (
                                    <div key={u.id} className={`client-item ${selectedCoachingUser?.id === u.id ? 'selected' : ''}`} onClick={() => handleSelectCoachingUser(u)}>
                                        <strong>{u.fullName}</strong>
                                        <div><small>{u.email}</small></div>
                                        <small style={{color: u.subscriptionStatus === 'active' ? '#ccff00' : '#aaa'}}>
                                            {u.subscriptionStatus === 'active' ? (PLAN_LABELS[u.subscriptionPlan] || 'Activ') : 'Inactiv'}
                                        </small>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="coaching-editor">
                            {selectedCoachingUser ? (
                                <>
                                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                                        <div>
                                            <h3>{selectedCoachingUser.fullName}</h3>
                                            <p style={{margin:0, fontSize:'0.9rem', color:'#aaa'}}>
                                                Status: <span style={{color: selectedCoachingUser.subscriptionStatus === 'active' ? '#ccff00' : 'red'}}>
                                                    {selectedCoachingUser.subscriptionStatus === 'active' ? 'ACTIV' : 'INACTIV'}
                                                </span>
                                            </p>
                                        </div>
                                        <button onClick={handleDeleteEntirePlan} style={{background: '#ff0000', color: 'white', border:'none', padding:'5px 10px', borderRadius:'5px', cursor:'pointer'}}>🗑️ RESET</button>
                                    </div>
                                    {loadingPlan ? <p>Loading...</p> : (
                                        <div className="plan-builder-wrapper">
                                            {renderBlockEditor(dietBlocks, setDietBlocks, "🥦 Plan Alimentar")}
                                            <hr className="divider"/>
                                            {renderBlockEditor(workoutBlocks, setWorkoutBlocks, "💪 Antrenament")}
                                            <button className="btn-publish full-width" onClick={handleSavePlan} disabled={isSavingPlan}>{isSavingPlan ? "Se salvează..." : "💾 SALVEAZĂ PLANUL"}</button>
                                        </div>
                                    )}
                                </>
                            ) : <div className="no-selection">Selectează un client.</div>}
                        </div>
                    </div>
                )}

                {/* --- REVIEWS TAB --- */}
                {activeTab === 'reviews' && (
                    <div className="admin-section">
                        <h2>Recenzii</h2>
                        <div className="admin-review-grid">
                            {reviews.map(r => <AdminReviewItem key={r.id} r={r} onDelete={handleDeleteReview} />)}
                        </div>
                    </div>
                )}

                {/* --- ARTICLES TAB (BLOG STYLE) --- */}
                {activeTab === 'articles' && (
                    <div className="admin-section">
                        <h2>Publică Articol (Blog Style)</h2>
                        <form onSubmit={handlePublishArticle} className="article-form">
                            <input
                                type="text"
                                placeholder="Titlu Articol"
                                value={articleTitle}
                                onChange={e=>setArticleTitle(e.target.value)}
                                required
                                style={{marginBottom:'20px', padding:'10px', fontSize:'1.1rem'}}
                            />

                            {/* EDITORUL DE BLOCURI REUTILIZABIL */}
                            {renderBlockEditor(articleBlocks, setArticleBlocks, "Conținut Articol (Text + Media)")}

                            <button type="submit" className="btn-publish" style={{marginTop:'20px'}} disabled={isUploading}>
                                {isUploading ? "Se publică..." : "Publică Articol"}
                            </button>
                        </form>

                        <div className="admin-list-scroll" style={{marginTop:'40px'}}>
                            <h3>Articole Existente</h3>
                            {articles.map(art => (
                                <div key={art.id} className="admin-item-row">
                                    <span>{art.title}</span>
                                    <button className="btn-delete-small" onClick={() => handleDeleteArticle(art.id)}>🗑️</button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* --- TRAIN & TRAVEL TAB --- */}
                {activeTab === 'traintravel' && (
                    <div className="admin-section">
                        <h2>Publică Train & Travel</h2>
                        <form onSubmit={handlePublishTrainTravel} className="article-form">
                            <input type="text" placeholder="Locație" value={trainTitle} onChange={e=>setTrainTitle(e.target.value)} required/>
                            <input type="file" id="fileInputTT" multiple accept="image/*,video/*" onChange={e=>setTrainFiles(e.target.files)} style={{color:'white'}}/>

                            {/* LINKURI YOUTUBE PENTRU POSTAREA NOUĂ */}
                            <div className="block-editor-container">
                                <h4 style={{margin:'0 0 10px 0'}}>▶️ Videoclipuri YouTube (opțional)</h4>
                                {trainYtLinks.map((link, i) => (
                                    <div key={i} style={{marginBottom:'12px'}}>
                                        <div style={{display:'flex', gap:'10px'}}>
                                            <input
                                                type="text"
                                                placeholder="Lipește link YouTube..."
                                                value={link}
                                                onChange={e => setTrainYtLinks(prev => prev.map((l, idx) => idx === i ? e.target.value : l))}
                                                style={{flex:1}}
                                            />
                                            <button type="button" className="btn-delete-small" onClick={() => setTrainYtLinks(prev => prev.filter((_, idx) => idx !== i))}>❌</button>
                                        </div>
                                        {getYouTubeId(link) && (
                                            <iframe
                                                style={{marginTop:'8px'}}
                                                width="220" height="124"
                                                src={`https://www.youtube.com/embed/${getYouTubeId(link)}`}
                                                title="preview" frameBorder="0" allowFullScreen
                                            />
                                        )}
                                    </div>
                                ))}
                                <div className="block-controls">
                                    <button type="button" onClick={() => setTrainYtLinks(prev => [...prev, ""])}>+ Adaugă link YouTube</button>
                                </div>
                            </div>

                            <textarea placeholder="Descriere..." rows="5" value={trainDesc} onChange={e=>setTrainDesc(e.target.value)} required></textarea>
                            <button type="submit" className="btn-publish" disabled={isUploadingTrain}>{isUploadingTrain ? "Se postează..." : "Postează"}</button>
                        </form>
                        <div className="admin-list-scroll">
                            {trainPosts.map(post => (
                                <div key={post.id} className="admin-post-container">
                                    <div className="admin-post-header">
                                        <h4>{post.title}</h4>
                                        <button className="btn-delete" onClick={() => handleDeleteTrainPost(post.id)}>Șterge Postare</button>
                                    </div>
                                    <div className="admin-media-gallery">
                                        {post.media && post.media.map((item, idx) => (
                                            <div key={idx} className="media-thumb-container">
                                                {item.type === 'video' ? <video src={item.url} className="media-thumb" /> : <img src={item.url} className="media-thumb" alt="" />}
                                                <button className="btn-delete-media" onClick={() => handleDeleteMedia(post.id, item)}>✕</button>
                                            </div>
                                        ))}
                                        {/* Clipuri YouTube existente la această postare */}
                                        {post.youtube && post.youtube.map((link, idx) => (
                                            <div key={'yt' + idx} className="media-thumb-container">
                                                <img src={`https://img.youtube.com/vi/${getYouTubeId(link)}/default.jpg`} className="media-thumb" alt="YouTube" />
                                                <button className="btn-delete-media" onClick={() => handleRemoveYtFromPost(post.id, link)}>✕</button>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Adaugă YouTube la o postare existentă */}
                                    <div style={{display:'flex', gap:'10px', marginTop:'10px'}}>
                                        <input
                                            type="text"
                                            placeholder="+ Link YouTube pentru această postare"
                                            value={postYtInputs[post.id] || ""}
                                            onChange={e => setPostYtInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                                            style={{flex:1, padding:'6px'}}
                                        />
                                        <button type="button" className="btn-publish" style={{padding:'6px 12px'}} onClick={() => handleAddYtToPost(post.id)}>Adaugă</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Admin;